import React, { useRef, useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useNavigate } from 'react-router-dom';
import { state } from '../../store/global';
import API from '../../lib/api';

import LoginRightWrapper from '../../components/common/LoginRightWrapper';
import LoginLeftSide from '../../components/common/LoginLeftSide';
import LoginFooter from '../../components/common/LoginFooter';
import NormalButton from '../../components/common/NormalButton';
import { useForceLightTheme } from '../../lib/theme';

export default function LoginPhoneVerify() {
  useForceLightTheme();

  const snap = useSnapshot(state);
  const navigate = useNavigate();

  const [verifyCode, setVerifyCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(59); // 60 seconds
  // Canal WhatsApp (respaldo cuando el operador filtra los SMS — 21-jul-2026)
  const [waBusy, setWaBusy] = useState(false);
  const [waSent, setWaSent] = useState(false);
  const inputRefs = useRef([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(interval); // cleanup
  }, [resendTimer]);
  const onInputVerifyCode = (index, value) => {
    if (!/^\d?$/.test(value)) return; // allow only one digit

    const updated = [...verifyCode];
    updated[index] = value;
    setVerifyCode(updated);

    // Move to next input if value is entered
    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Move to previous input if value is entered
    if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const onConfirmPhoneVerify = async () => {
    const code = verifyCode.join('');
    console.log('code: ', code);

    await API.post('auth/verifyCode', {
      phonenumber:
        snap.user.phonenumber.nationality.area_code +
        snap.user.phonenumber.number,
      code: code,
    })
      .then((res) => {
        localStorage.setItem('token', res.data.token);
        state.setAuthentication(res.data.phoneVerified);
        // Cuentas rol Empresas aterrizan en su dashboard (FlowexPay Business)
        navigate(snap.user?.role === 'business' ? '/business' : '/dashboard');
      })
      .catch((err) => {
        console.log('err: ', err);
        setError(true);
      });
  };
  const onResendVerifyCode = async () => {
    await API.post('auth/sendCodeToPhone', {
      phonenumber:
        snap.user.phonenumber.nationality.area_code +
        snap.user.phonenumber.number,
    })
      .then(() => {
        setResendTimer(59);
      })
      .catch((err) => {
        console.log('err: ', err);
      });
  };
  // El SMS puede ser filtrado en silencio por el operador (Movistar/Claro tras
  // muchos codigos). WhatsApp es el canal de rescate: mismo codigo, otra via.
  const onSendWhatsApp = async () => {
    if (waBusy) return;
    setWaBusy(true); setWaSent(false);
    await API.post('auth/sendCodeToPhone', {
      phonenumber:
        snap.user.phonenumber.nationality.area_code +
        snap.user.phonenumber.number,
      channel: 'whatsapp',
    })
      .then(() => { setWaSent(true); setResendTimer(59); })
      .catch((err) => { console.log('err: ', err); })
      .finally(() => setWaBusy(false));
  };

  const onGoToPage = (path) => {
    navigate(path);
  };
  const isFullInputed = () => {
    return verifyCode.every((val) => val !== '');
  };

  return (
    <div className="flex min-h-screen">
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        {/* Modal content with exact size */}
        <div className="relative z-10 bg-white rounded-2xl shadow-xl w-[1044px] h-[700px] overflow-hidden">
          <div className="flex md:flex-row w-full h-full">
            {/* Left Side - Image */}
            <LoginLeftSide />

            {/* Right Side - Form */}
            <div className="w-full h-full flex flex-col justify-between md:w-1/2 p-10 bg-[#F9F9F9]">
              <LoginRightWrapper
                onGoToPage={(path) => onGoToPage(path)}
                backPath="/login/phoneset"
              />

              <div className="h-80">
                <div className="space-y-8 font-[poppins] font-semibold pl-0 pr-0 md:pl-14 md:pr-14">
                  <div className="space-y-4">
                    <h2 className="text-xl text-[#333333] font-bold leading-[20px] tracking-[0%]">
                      Código enviado
                    </h2>
                    <div className="text-sm text-[#717680] font-normal leading-[20px] tracking-[0%]">
                      <h3>Ingresa el código que enviamos a tu teléfono</h3>
                      <p className="font-semibold">
                        {snap.user.phonenumber.nationality.area_code}*********
                        {snap.user.phonenumber.number.slice(-4)}
                      </p>
                    </div>
                  </div>

                  <div className="w-full space-y-4">
                    <div className="h-12">
                      <div className="h-12 flex items-center justify-between">
                        {verifyCode.map((val, idx) => (
                          <input
                            key={idx}
                            ref={(el) => (inputRefs.current[idx] = el)}
                            type="text"
                            value={val}
                            onChange={(e) =>
                              onInputVerifyCode(idx, e.target.value)
                            }
                            placeholder="_"
                            className={`w-12 h-12 placeholder-[#2A59C6] p-4 rounded-[10px] text-[20px] text-center focus:outline-none focus:border-[#414651] ${
                              error
                                ? 'border border-[#f04438] text-[#f04438] bg-[#FEF3F2]'
                                : isFullInputed()
                                ? 'border border-[#039855] text-[#039855] bg-[#F9F9F9]'
                                : 'bg-[#E6EBF7]'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {error && (
                      <div className="text-[#f04438] font-normal text-sm leading-[20px] tracking-[0%]">
                        El código ingresado no es correcto. Verifícalo e intenta
                        nuevamente.
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <NormalButton
                      disabled={isFullInputed() ? false : true}
                      customClass={`w-full ${
                        isFullInputed()
                          ? 'bg-[#092F7B] text-white'
                          : 'bg-[#F5F5F5] text-[#A4A7AE]'
                      }`}
                      text="Activar verificación"
                      onClick={onConfirmPhoneVerify}
                    />

                    <NormalButton
                      disabled={resendTimer ? true : false}
                      customClass={`w-full ${
                        resendTimer
                          ? 'bg-[#FDFDFD] text-[#092F7B]'
                          : 'bg-[#092F7B] text-white'
                      } border border-[#092F7B]`}
                      text={`Reenviar en 00:${
                        resendTimer < 10 ? '0' + resendTimer : resendTimer
                      }`}
                      onClick={onResendVerifyCode}
                    />

                    <button
                      onClick={onSendWhatsApp}
                      disabled={waBusy}
                      className="w-full py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
                      style={{ backgroundColor: '#25D366', color: '#fff' }}
                    >
                      {waBusy ? 'Enviando...' : waSent ? 'Código enviado por WhatsApp ✅ (reenviar)' : '¿No te llega el SMS? Recibir por WhatsApp'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Login footer */}
              <LoginFooter onClick={() => navigate('/contact')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
